<template>
  <PanelPage ref="useAlerts" :loading-overlay="loadingOverlay" md="12">
    <v-row>
      <v-col>
        <v-card>
          <v-card-title>{{ props.title }}</v-card-title>
          <v-card-title>
            <v-row class="mb-0">
              <v-col>
                <v-file-input
                  accept="image/*"
                  label="Media file to upload"
                  v-model="uploadFile"
                  hide-details
                />
              </v-col>
              <v-col>
                <v-text-field
                  label="Media Name"
                  v-model="mediaName"
                  hide-details
                />
              </v-col>
              <v-col cols="auto">
                <v-btn class="fill-height" :disabled="uploadFile === null" @click="sendFile">
                  Submit
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card>
          <v-container>
            <v-row>
              <v-col>
                <v-card-title>
                  Media already in the system
                </v-card-title>
              </v-col>
              <v-col cols="auto">
                <v-switch
                  label="Show Images"
                  color="primary"
                  v-model="showImages"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="2" v-for="media in medias" :key="media['id']">
                <v-card class="fill-height d-flex flex-column mediacard"
                        @click="showMediaInOverlay(media)">
                  <v-card-text><b>{{ `${media['id']} - ${media['alias']}` }}</b></v-card-text>
                  <v-img v-if="showImages" class="mb-4" height="100px" width="100%" :src="media['url']"/>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-col>
    </v-row>
    <v-overlay v-model="showMediaOverlay" class="d-flex justify-center align-center">
      <v-card>
        <v-row>
          <v-col>
            <v-card-title>Media {{ mediaToShow['id'] }} - {{ mediaToShow['alias'] }}</v-card-title>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="d-flex justify-center">
            <v-img :width="cardWidth + 'px'" :height="cardHeight + 'px'" max-width="70vw" max-height="70vh"
                   ref="cardImage"
                   class="ma-2 align-content-center"
                   :src="mediaToShow['url']" @load="onImageLoad"/>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-card-actions class="justify-end">
              <v-btn prepend-icon="mdi-delete" text="Delete" @click="deleteMedia(mediaToShow['id'])"/>
            </v-card-actions>
          </v-col>
        </v-row>
      </v-card>
    </v-overlay>
  </PanelPage>
</template>

<script setup>
import { onMounted, ref } from "vue";
import axios from 'axios';
import { objComparatorNumber, readFileAsBase64 } from "../utils.js";
import PanelPage from "./PanelPage.vue";

// State
const useAlerts = ref(null);
const loadingOverlay = ref(false);
const uploadFile = ref(null);
const mediaName = ref(null);
const medias = ref([]);
const showMediaOverlay = ref(false);
const mediaToShow = ref(null);
const showImages = ref(true);
const cardImage = ref(null);
const cardWidth = ref(100);
const cardHeight = ref(100);

function onImageLoad() {
  cardWidth.value = cardImage.value.image.naturalWidth;
  cardHeight.value = cardImage.value.image.naturalHeight;
}

// baseurl: example "https://testdomain.com/internal/current/mediaimages/"
const props = defineProps(['baseurl', 'title']);

function showMediaInOverlay(media) {
  mediaToShow.value = media;
  showMediaOverlay.value = true;
}

async function fetchMedias() {
  try {
    const res = await axios.get(props.baseurl, {
      withCredentials: true
    });
    return res.data.sort((a, b) => objComparatorNumber(a, b, 'id')).map(el => {
      return { ...el };
    });
  } catch (e) {
    useAlerts.value.errorAlert('Impossible to load Medias', e.toString());
  }
}

async function retrieveMedias() {
  loadingOverlay.value = true;
  medias.value = await fetchMedias();
  loadingOverlay.value = false;
}

onMounted(retrieveMedias);

async function sendFile() {
  loadingOverlay.value = true;
  const filebase64 = await readFileAsBase64(uploadFile.value);
  const splitName = uploadFile.value.name.split('.');
  const fileExtension = splitName.pop();
  const payload = {
    alias: mediaName.value || splitName.join('.'),
    image: filebase64,
    file_extension: fileExtension
  };
  await axios.post(props.baseurl, payload, {
    withCredentials: true
  }).then(response => {
    if (response.status !== 201) {
      useAlerts.value.warningAlert(`Anomalous return code!`, `${response.status} ${response.data}`);
      return;
    }
    useAlerts.value.successAlert(`New Media uploaded!`, `MediaImage ID = ${response.data.id}`);
  }).catch(error => {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      useAlerts.value.errorAlert(`Error ${error.response.status}`, `${JSON.stringify(error.response.data)}`);
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser
      useAlerts.value.errorAlert(`No response received!`, `${JSON.stringify(error.request)}`);
    } else {
      // Something happened in setting up the request that triggered an Error
      useAlerts.value.errorAlert(`Unexpected Error!`, `${JSON.stringify(error.message)}`);
    }
  }).finally(async () => {
    medias.value = await fetchMedias();
    loadingOverlay.value = false;
  });
}

async function deleteMedia(media_id) {
  loadingOverlay.value = true;
  await axios.delete(`${props.baseurl}${media_id}/`, {
    withCredentials: true
  }).then(response => {
    if (response.status !== 204) {
      useAlerts.value.warningAlert(`Anomalous return code!`, `${response.status} ${response.data}`);
      return;
    }
    useAlerts.value.successAlert(`Media deleted!`, `MediaImage ID = ${response.data.id}`);
    showMediaOverlay.value = false;
  }).catch(error => {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      useAlerts.value.errorAlert(`Error ${error.response.status}`, `${JSON.stringify(error.response.data)}`);
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser
      useAlerts.value.errorAlert(`No response received!`, `${JSON.stringify(error.request)}`);
    } else {
      // Something happened in setting up the request that triggered an Error
      useAlerts.value.errorAlert(`Unexpected Error!`, `${JSON.stringify(error.message)}`);
    }
  }).finally(async () => {
    medias.value = await fetchMedias();
    loadingOverlay.value = false;
  });
}

</script>

<style scoped>
.mediacard {
  transition: box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out;
  box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12); /* Stealed the second and third from elevation:4 */
}

.mediacard:hover {
  transform: translateY(-5px); /* Lift the card up on hover */
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12);
}

.mediacard:active {
  animation: click-wave 0.4s ease-out;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12); /* Smaller shadow for a pressed effect */
}

@keyframes click-wave {
  0% {
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12);
  }
  50% {
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.4), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12);
  }
  100% {
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12);
  }
}

</style>
